import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Box, Button, TextField } from "@mui/material";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-datetime/css/react-datetime.css";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ImageModal from "components/ImageModal";
import EmptyData from "components/EmptyData";
import GoogleMapModal from "DialogBox/googlemap";
import apiClient from "Utils/apiClient";

import staticmap from "assets/images/map.png";
import tripMonitorAuthorTable from "layouts/tables/data/tripMonitorAuthorTable";
import { useMaterialUIController } from "context";
import { getTimeFromTimestamp } from "Utils/helper";
import File from "Utils/file";
import ShowChatData from "Utils/showChatData";
import EscortReport from "Utils/escortReport";
import { MenuIntroduction } from "layouts/tables/MenuIntroduction";
import NotificationModal from "Utils/NotificationModal";

function ShortDistance() {
  const navigate = useNavigate();
  const { getUserState, allData } = useSelector((state) => state.data);
  const [controller] = useMaterialUIController();
  const { columns } = tripMonitorAuthorTable();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onClickImageData, setOnClickImageData] = useState("");
  const [openGmap, setOpenGmap] = useState(false);
  const [markerList, setMarkerList] = useState([]);
  const [userLocation, setUserLocation] = useState([]);

  const [checkSuperAdmin, setCheckSuperAdmin] = useState(null);
  const [adminState, setAdminState] = useState(null);

  const [openNotification, setOpenNotification] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState(null);

  const handleError = (msg) => toast.error(msg);

  const handleImageClick = useCallback((url) => {
    setIsModalOpen(true);
    setOnClickImageData(url);
  }, []);

  const handleStops = useCallback((stopData) => {
    const stops = Object.values(stopData);
    setUserLocation(stops);
    setMarkerList(stops.filter((d) => !d.is_moving));
    setOpenGmap(true);
  }, []);

  const closeModals = useCallback(() => {
    setIsModalOpen(false);
    setOpenGmap(false);
    setMarkerList([]);
    setUserLocation([]);
  }, []);

  const getLastShortDistanceEscort = (shortEscortObj) => {
    if (!shortEscortObj) return null;

    const escortArray = Array.isArray(shortEscortObj)
      ? shortEscortObj
      : Object.values(shortEscortObj);

    //console.log("escortArray", escortArray);

    if (escortArray.length === 0) return null;

    escortArray.sort((a, b) => (b.currentTimeStamp || 0) - (a.currentTimeStamp || 0));

    return escortArray[0];
  };

  const getSortedNotifications = (notificationsObj) => {
    if (!notificationsObj) return [];

    //   console.log("Notification get", Object.values(notificationsObj));

    return Object.values(notificationsObj).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  };

  const handleOpenNotification = (notifications) => {
    setSelectedNotifications(notifications);
    setOpenNotification(true);
  };

  const handleCloseNotification = () => {
    setOpenNotification(false);
    setSelectedNotifications(null);
  };

  const buildRow = (user, shortTrip, stops, index) => ({
    Sr: 0,
    Name: user.userName || "Unknown",
    Email: user.userEmail || "N/A",
    Mobile: user.userPhone || "N/A",
    VehicleReg: shortTrip.vehicleReg || shortTrip.driverId?.regNo || "N/A",
    VehicleImage: (
      <img
        onClick={() => handleImageClick(shortTrip.imgVehicle || shortTrip.driverId?.imgVehicle)}
        src={shortTrip.imgVehicle || shortTrip.driverId?.imgVehicle}
        alt="vehicle"
        style={{ width: 50, height: 50, borderRadius: "50%", cursor: "pointer" }}
      />
    ),
    State: user.state || "Unknown",
    TripDate: shortTrip.ArrivalDate || shortTrip.Date || shortTrip.createdAt || "N/A",
    IsMoving: (() => {
      const isLegacyMoving = shortTrip.Trip_Status === "Moving";
      const isLegacyArrived = shortTrip.Trip_Status === "Arrived";
      const isActive = shortTrip.status === "ACTIVE";
      const isCompleted = shortTrip.status === "COMPLETED";

      const isRunning = isLegacyMoving || isActive;
      const hasArrived = isLegacyArrived || isCompleted;

      return (
        <button
          style={{
            backgroundColor: isRunning ? "green" : hasArrived ? "yellow" : "red",
            color: hasArrived ? "black" : "white",
            padding: "6px 16px",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: "500",
          }}
        >
          {isRunning ? "Running" : hasArrived ? "Arrived" : "Stopped"}
        </button>
      );
    })(),
    TripTime: getTimeFromTimestamp(shortTrip.currentTimeStamp || shortTrip.startTime) || "N/A",
    TripEndTime: getTimeFromTimestamp(shortTrip?.tripendtime || shortTrip.endTime) || "N/A",
    TripType: <h5>Short Distance</h5>,
    currentTimeStamp: shortTrip.currentTimeStamp || shortTrip.startTime || shortTrip.createdAt,
    StartRide: (
      <img
        onClick={() => handleStops(stops)}
        src={staticmap}
        alt="map"
        style={{ width: 50, height: 50, borderRadius: "50%", cursor: "pointer" }}
      />
    ),
    Notification: (() => {
      const lastEscort = getLastShortDistanceEscort(user?.ShortDistance_Escort);

      return lastEscort?.notifications ? (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => handleOpenNotification(getSortedNotifications(lastEscort.notifications))}
        >
          Show Notification
        </button>
      ) : (
        "No Notification"
      );
    })(),
    File: <File props={shortTrip} />,
    Chat: <ShowChatData props={shortTrip} />,
    Report: <EscortReport props={user} />,
    EndRide: <MenuIntroduction />,
  });

  const getData = useCallback(async () => {
    const adminData = JSON.parse(localStorage.getItem("user"));
    if (!adminData) return navigate("/authentication/sign-in");

    const userState = adminData.state;
    const isSuperAdmin = adminData.superAdmin;
    setCheckSuperAdmin(isSuperAdmin);
    setAdminState(userState);

    setLoading(true);
    try {
      const response = await apiClient.get("/users/short-escorts");
      const users = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

      console.log("[ShortDistance] short-escorts API response:", response?.data);
      console.log("[ShortDistance] short-escorts users count:", users.length);

      if (!users.length) return handleError("No short escort data found.");

      const newRows = [];

      users.forEach((user) => {
        const shortEscorts = Array.isArray(user?.ShortDistance_Escort)
          ? user.ShortDistance_Escort
          : Object.values(user?.ShortDistance_Escort || {});
        const { state } = user;

        const allowed =
          (isSuperAdmin && (!getUserState || getUserState === "All State")) ||
          (isSuperAdmin && state === getUserState) ||
          (!isSuperAdmin && state === userState);

        if (!allowed) return;

        shortEscorts.forEach((shortTrip) => {
          console.log("[ShortDistance] short escort row data:", {
            userId: user?.userId || user?._id || null,
            userName: user?.userName || "Unknown",
            shortTrip,
          });
          newRows.push(
            buildRow(
              user,
              shortTrip,
              shortTrip?.stopspoints || shortTrip?.stops || {},
            ),
          );
          // console.log("Check User", user);
        });
      });

      const sortedRows = newRows.sort((a, b) => {
        const timeA = new Date(a.currentTimeStamp || 0).getTime();
        const timeB = new Date(b.currentTimeStamp || 0).getTime();
        return timeB - timeA;
      });
      const numberedRows = sortedRows.map((row, idx) => ({ ...row, Sr: idx + 1 }));

      setRows(numberedRows);
    } catch (err) {
      handleError(err?.response?.data?.message || "Error fetching short escort data from backend.");
    } finally {
      setLoading(false);
    }
  }, [getUserState, handleStops, handleImageClick, navigate]);

  useEffect(() => {
    getData();
  }, [getData, allData, getUserState]);

  const filteredRows = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    return rows.filter(
      (r) =>
        r.Name.toLowerCase().includes(search) ||
        r.Email.toLowerCase().includes(search) ||
        r.Mobile.toLowerCase().includes(search)
    );
  }, [rows, searchTerm]);

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Box display="flex" justifyContent="flex-end" mb={5} px={2}>
          <TextField
            label="Search by name, email, or mobile"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
          />
        </Box>

        <Grid container spacing={6} style={{ position: "relative", minHeight: "60vh" }}>
          {loading ? (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              Loading...
            </Box>
          ) : (
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <ToastContainer />
                  <MDTypography variant="h6" color="white">
                    {checkSuperAdmin
                      ? getUserState === "" || getUserState === "All State"
                        ? "All States"
                        : `${getUserState} State`
                      : `${adminState} State`}{" "}
                    Short Distance Escort
                  </MDTypography>
                </MDBox>

                <MDBox pt={3}>
                  {filteredRows.length ? (
                    <DataTable
                      table={{ columns, rows: filteredRows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  ) : (
                    <EmptyData />
                  )}
                </MDBox>
              </Card>

              <ImageModal
                onClickImageData={onClickImageData}
                onClose={() => setIsModalOpen(false)}
                isModalOpen={isModalOpen}
              />
              {openGmap && (
                <GoogleMapModal
                  open={openGmap}
                  onClose={closeModals}
                  markerList={markerList}
                  userLocation={userLocation}
                />
              )}

              <NotificationModal
                open={openNotification}
                handleClose={handleCloseNotification}
                notifications={selectedNotifications}
              />
            </Grid>
          )}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default ShortDistance;
